import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Briefcase, CreditCard, Phone, Wrench, ShoppingCart, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../contexts/CartContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { totalItems } = useCart();
  const location = useLocation();
  const onHome = location.pathname === '/';

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Anchor links work only on the home page; otherwise prefix with / to navigate first.
  const anchor = (id) => (onHome ? `#${id}` : `/#${id}`);

  const navItems = [
    { name: 'Inicio', href: anchor('inicio'), icon: Home, isExternal: false },
    { name: 'Servicios', href: anchor('servicios'), icon: Briefcase, isExternal: false },
    { name: 'Paquetes', href: anchor('paquetes'), icon: CreditCard, isExternal: false },
    { name: 'Tienda', href: '/tienda', icon: Wrench, isExternal: true },
    { name: 'Guías', href: '/guias', icon: BookOpen, isExternal: true },
    { name: 'Contacto', href: anchor('contacto'), icon: Phone, isExternal: false },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="inline-block group">
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl industrial-logo cursor-pointer">DRYWALL</h1>
                <span
                  className="bg-red-600 text-white text-sm sm:text-base font-extrabold tracking-[0.2em] px-2 py-0.5 rounded-md shadow-sm group-hover:bg-red-700 transition-colors"
                  style={{ fontFamily: "'Orbitron', monospace" }}
                >
                  TOOLS
                </span>
              </div>
              <span className="logo-sub block text-[9px] sm:text-[10px] text-slate-400 mt-0.5">
                Drywall Desing LLC
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <div className="flex items-baseline space-x-6">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                const linkClass = "text-slate-600 hover:text-red-600 px-2 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2 group";
                if (item.isExternal) {
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={linkClass}
                    >
                      <IconComponent size={16} className="group-hover:scale-110 transition-transform duration-200" />
                      {item.name}
                    </Link>
                  );
                }
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={linkClass}
                  >
                    <IconComponent size={16} className="group-hover:scale-110 transition-transform duration-200" />
                    {item.name}
                  </a>
                );
              })}
            </div>

            {/* Cart icon (desktop) */}
            <Link
              to="/carrito"
              data-testid="nav-cart-btn"
              className="relative ml-2 p-2 rounded-md text-slate-600 hover:text-red-600 hover:bg-slate-100 transition-colors"
              aria-label="Carrito"
            >
              <ShoppingCart size={20} />
              {totalItems > 0 && (
                <span
                  data-testid="nav-cart-badge"
                  className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
                >
                  {totalItems}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile menu button + cart */}
          <div className="md:hidden flex items-center gap-2">
            <Link
              to="/carrito"
              data-testid="nav-cart-btn-mobile"
              className="relative p-2 text-slate-600"
              aria-label="Carrito"
            >
              <ShoppingCart size={22} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMenu}
              className="text-slate-600 hover:text-red-600"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              const linkClass = "text-slate-600 hover:text-red-600 px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 flex items-center gap-3";
              if (item.isExternal) {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={linkClass}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <IconComponent size={20} />
                    {item.name}
                  </Link>
                );
              }
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className={linkClass}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <IconComponent size={20} />
                  {item.name}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
